import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import type {
  DeviceRequest,
  DeviceResponse,
  SelectOption,
} from 'src/api/ProactApiClient';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {BackBar} from 'src/app/layout/BackBar';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  useCreateDeviceMutation,
  useGetDeviceQuery,
  useUpdateDeviceMutation,
} from 'src/redux/queries/device';
import {useGetEquipmentTypeSelectOptionsQuery} from 'src/redux/queries/equipmentType';
import {FULL_OPTIONS_REQUEST} from 'src/redux/queries/inspectionRecord';
import {useGetSiteSelectOptionsQuery} from 'src/redux/queries/site';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  DeviceDocumentTitle,
  DeviceFormMeta,
  MapDeviceResponseToDeviceRequest,
} from './deviceHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface DeviceFormProps {
  id?: number;
  mode: 'create' | 'edit';
  /** Renders form only (no PageHeader/BackBar), for hosting in a modal/drawer. */
  embedded?: boolean;
  /** Embedded-mode create callback — called with the created device instead of navigating. */
  onCreated?: (device: DeviceResponse) => void;
  /** Embedded-mode cancel callback, used instead of `navigate`. */
  onCancelled?: () => void;
  /** Embedded-mode site pin (e.g. the host inspection's site) — pre-fills and locks the Site field. */
  lockedSiteId?: number;
  /** Embedded-mode create override, routes through a different mutation than `createDevice`. Ignored in edit mode. */
  onSubmitOverride?: (model: DeviceRequest) => Promise<DeviceResponse>;
  /** Injected Site/Equipment-Type options — e.g. the inspection-scoped offline
   * source. When set, the global select-option queries are skipped. */
  optionsOverride?: {
    sites: SelectOption[];
    equipmentTypes: SelectOption[];
    isLoading: boolean;
  };
}

export const DeviceForm: React.FC<DeviceFormProps> = ({
  id,
  mode,
  embedded = false,
  onCreated,
  onCancelled,
  lockedSiteId,
  onSubmitOverride,
  optionsOverride,
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';
  const fromSiteId = isCreateMode
    ? lockedSiteId
      ? String(lockedSiteId)
      : searchParams.get('siteId')
    : null;

  const {data: sites, isLoading: isSitesLoading} = useGetSiteSelectOptionsQuery(
    FULL_OPTIONS_REQUEST,
    {skip: !!optionsOverride}
  );

  const {data: equipmentTypes, isLoading: isEquipmentTypesLoading} =
    useGetEquipmentTypeSelectOptionsQuery(FULL_OPTIONS_REQUEST, {
      skip: !!optionsOverride,
    });

  const siteOptions = optionsOverride?.sites ?? sites?.data ?? [];
  const equipmentTypeOptions =
    optionsOverride?.equipmentTypes ?? equipmentTypes?.data ?? [];

  const {data: fetchedDevice, isLoading: isDeviceLoading} = useGetDeviceQuery(
    id!,
    {skip: !isEditMode || id === undefined}
  );

  const [createDevice] = useCreateDeviceMutation();
  const [updateDevice] = useUpdateDeviceMutation();

  const item: DeviceResponse | undefined = fetchedDevice;
  const isOptionsLoading = optionsOverride
    ? optionsOverride.isLoading
    : isSitesLoading || isEquipmentTypesLoading;
  const isLoading = isEditMode && isDeviceLoading;

  const formMeta = useMemo<IFormFieldV2[]>(() => {
    if (isOptionsLoading) return [];
    const meta = DeviceFormMeta(siteOptions, equipmentTypeOptions);
    if (!lockedSiteId) return meta;
    return meta.map(field =>
      field.name === 'siteId' ? {...field, disabled: true} : field
    );
  }, [isOptionsLoading, siteOptions, equipmentTypeOptions, lockedSiteId]);

  const form = useEntitySyncedForm({
    formMeta,
    entity: fetchedDevice,
    toFormData: toFormDataV2,
    initialData: fromSiteId ? {siteId: fromSiteId} : undefined,
  });

  const handleSave = async () => {
    setIsSavePending(true);

    try {
      const values = form.getFieldValues();
      const model: DeviceRequest = MapDeviceResponseToDeviceRequest({
        ...values,
        id: item?.id,
      } as DeviceResponse);

      let response: DeviceResponse;

      if (isCreateMode) {
        response = onSubmitOverride
          ? await onSubmitOverride(model)
          : await createDevice(model).unwrap();
        setAlert(`${response.name} was successfully created`, 'success');
      } else {
        response = await updateDevice(model).unwrap();
        setAlert(
          `${item?.name ?? 'Device'} was successfully updated`,
          'success'
        );
      }

      if (embedded) {
        onCreated?.(response);
      } else if (isCreateMode && fromSiteId) {
        navigate(PathUtility.SitesView(fromSiteId), {replace: true});
      } else {
        navigate(PathUtility.DevicesView(response.id), {replace: true});
      }
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack} = useBackNavigation(
    isEditMode && id
      ? PathUtility.DevicesView(id)
      : fromSiteId
      ? PathUtility.SitesView(fromSiteId)
      : PathUtility.Inspections('devices')
  );
  const handleCancel = () => {
    if (embedded) {
      onCancelled?.();
      return;
    }
    goBack();
  };

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = isCreateMode ? 'New Device' : item?.name ?? 'Edit Device';

  if (embedded) {
    return (
      <Stack>
        <ProactForm form={form}>
          <ProactFormGrid />
        </ProactForm>
        <Group justify="end">
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create Device' : 'Save Changes'}
          </PageHeader.Action>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack>
      <BackBar
        fallbackTo={
          isEditMode && id
            ? PathUtility.DevicesView(id)
            : fromSiteId
            ? PathUtility.SitesView(fromSiteId)
            : PathUtility.Inspections('devices')
        }
        fallbackLabel={
          isEditMode
            ? 'Back to Device'
            : fromSiteId
            ? 'Back to Site'
            : 'Back to Devices'
        }
        breadcrumbs={[
          {label: DeviceDocumentTitle, to: PathUtility.Inspections('devices')},
          ...(isEditMode && item
            ? [{label: item.name ?? 'Device', to: PathUtility.DevicesView(id!)}]
            : []),
          {label: isCreateMode ? 'New Device' : 'Edit'},
        ]}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkInspections.icon}>
          {pageTitle}
        </PageHeader.Title>

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create Device' : 'Save Changes'}
          </PageHeader.Action>
        </PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <ProactFormGrid />
      </ProactForm>

      <Group>
        <PageHeader.Action variant="cancel" onClick={handleCancel}>
          Cancel
        </PageHeader.Action>
        <PageHeader.Action
          variant={isCreateMode ? 'create' : 'save'}
          onClick={handleValidateAndSave}
          loading={isSavePending}>
          {isCreateMode ? 'Create Device' : 'Save Changes'}
        </PageHeader.Action>
      </Group>
    </Stack>
  );
};
